import React from 'react';
import { Check, Lock, Timer, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Text } from '@/components/flup';
import { Slot, SlotView, formatTime } from './slotUtils';

/**
 * One slot on the board.
 *
 * The whole tile is the hit target — a vendor scanning a 40-tile evening should
 * never have to aim at a checkbox. Selected is UI green, matching every other
 * "this is what you picked" surface in the app; it is deliberately *not* brand
 * teal, which is reserved for the actions in the bar below.
 */

/**
 * A slot's resting skin.
 *
 * Open slots carry a light green wash so the sellable part of a day is visible
 * at a glance — on a board of forty tiles the vendor should be able to find
 * what they can act on without reading a single label.
 */
const SKINS: Record<SlotView['state'], string> = {
  available: 'bg-flup-success-soft border-flup-success/25 hover:border-flup-green',
  blocked: 'bg-flup-danger-soft border-transparent hover:border-flup-danger/40',
  booked: 'bg-flup-info-soft border-transparent',
  hold: 'bg-flup-warning-soft border-transparent',
  expiring: 'bg-flup-warning-soft border-transparent',
  expired: 'bg-flup-surface-alt border-transparent',
};

const LABEL_TONE: Record<SlotView['state'], string> = {
  available: 'text-flup-success',
  blocked: 'text-flup-danger',
  booked: 'text-flup-info',
  hold: 'text-flup-warning',
  expiring: 'text-flup-warning',
  expired: 'text-flup-ink-3',
};

const STATE_ICON: Partial<Record<SlotView['state'], React.ComponentType<any>>> = {
  booked: User,
  blocked: Lock,
  hold: Timer,
  expiring: Timer,
};

interface SlotTileProps {
  slot: Slot;
  view: SlotView;
  selected: boolean;
  onToggle: (slot: Slot) => void;
}

const SlotTile: React.FC<SlotTileProps> = ({ slot, view, selected, onToggle }) => {
  const Icon = STATE_ICON[view.state];

  return (
    <button
      type="button"
      disabled={!view.selectable}
      aria-pressed={selected}
      onClick={() => view.selectable && onToggle(slot)}
      className={cn(
        'relative flex flex-col items-start gap-0.5 p-3 rounded-flup-md border text-left overflow-hidden',
        'transition-colors duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flup-brand focus-visible:ring-offset-2',
        // Selected is a solid green fill, not another tint: the resting open
        // state is already light green, so a tint-on-tint selection would be
        // almost invisible on exactly the slots vendors select most.
        selected ? 'bg-flup-green border-flup-green' : SKINS[view.state],
        view.selectable ? 'cursor-pointer' : 'cursor-default',
        !view.selectable && 'opacity-85',
      )}
    >
      <Text
        variant="labelSemi"
        as="span"
        className={cn('nums', selected && 'text-white')}
      >
        {formatTime(slot.start_time)}
      </Text>
      <Text
        variant="micro"
        tone={selected ? 'inherit' : 'tertiary'}
        as="span"
        className={cn('nums', selected && 'text-white/70')}
      >
        to {formatTime(slot.end_time)}
      </Text>

      <div className="flex items-baseline gap-1.5 mt-1.5">
        <Text variant="statSm" as="span" className={selected ? 'text-white' : undefined}>
          ₹{Math.round(view.price)}
        </Text>
        {view.wasPrice ? (
          <Text
            variant="micro"
            tone={selected ? 'inherit' : 'tertiary'}
            as="span"
            className={cn('line-through nums', selected && 'text-white/60')}
          >
            ₹{Math.round(view.wasPrice)}
          </Text>
        ) : null}
      </div>

      <span className="flex items-center gap-1 mt-0.5">
        {Icon ? (
          <Icon
            className={cn('w-3 h-3', selected ? 'text-white/80' : LABEL_TONE[view.state])}
            aria-hidden
          />
        ) : null}
        <Text
          variant="micro"
          tone="inherit"
          as="span"
          className={selected ? 'text-white/80' : LABEL_TONE[view.state]}
        >
          {view.state === 'hold' && view.holdMinutesLeft != null
            ? `On hold · ${view.holdMinutesLeft}m`
            : view.label}
        </Text>
      </span>

      {view.discountLabel && !selected ? (
        <span className="absolute top-2 right-2 px-1.5 h-[18px] grid place-items-center rounded-flup-xs bg-flup-brand-soft">
          <Text variant="micro" as="span" className="text-flup-brand-ink text-[10px]">
            {view.discountLabel}
          </Text>
        </span>
      ) : null}

      {selected ? (
        <span className="absolute top-2 right-2 grid place-items-center w-5 h-5 rounded-pill bg-white/25">
          <Check className="w-3 h-3 text-white" aria-hidden />
        </span>
      ) : null}
    </button>
  );
};

export default React.memo(SlotTile);
